import { Fragment, type ReactNode } from 'react'

/**
 * Inicjały z pełnego imienia i nazwiska - max 2 znaki (pierwsze litery
 * pierwszego i ostatniego członu). Używane jako fallback-avatar.
 */
export function initials(fullName: string | null | undefined): string {
  const parts = (fullName ?? '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0]!.slice(0, 1).toUpperCase()
  return (parts[0]!.slice(0, 1) + parts[parts.length - 1]!.slice(0, 1)).toUpperCase()
}

/**
 * Deterministyczny kolor avatara z id użytkownika (stała paleta Tailwind).
 * Bez Math.random, żeby ten sam user miał zawsze ten sam kolor.
 */
const AVATAR_COLORS = [
  'bg-sky-500',
  'bg-emerald-500',
  'bg-violet-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-teal-500',
  'bg-indigo-500',
  'bg-fuchsia-500',
]

export function avatarColor(userId: number): string {
  const i = ((userId % AVATAR_COLORS.length) + AVATAR_COLORS.length) % AVATAR_COLORS.length
  return AVATAR_COLORS[i]!
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Renderuje treść wiadomości, podświetlając wzmianki `@Imię Nazwisko`,
 * które pasują do nazw przekazanych w `mentionNames` (nazwy wzmiankowanych
 * userów, rozwiązane z id). Dłuższe nazwy dopasowujemy najpierw, żeby
 * uniknąć częściowych trafień. Reszta tekstu zostaje bez zmian.
 */
export function renderBody(body: string, mentionNames: string[]): ReactNode {
  const names = mentionNames
    .map(n => n.trim())
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)

  if (names.length === 0) return body

  const re = new RegExp(`@(?:${names.map(escapeRegExp).join('|')})`, 'g')
  const out: ReactNode[] = []
  let last = 0
  let m: RegExpExecArray | null
  let key = 0
  while ((m = re.exec(body)) !== null) {
    if (m.index > last) out.push(<Fragment key={key++}>{body.slice(last, m.index)}</Fragment>)
    out.push(
      <span
        key={key++}
        data-mention
        className="rounded bg-sky-100 px-1 font-medium text-sky-700"
      >
        {m[0]}
      </span>,
    )
    last = m.index + m[0].length
  }
  if (last < body.length) out.push(<Fragment key={key++}>{body.slice(last)}</Fragment>)
  return out
}
